import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportTickets, SupportTicket } from '../../crm-support-ticket/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import type { DashboardScope } from '../contexts/DashboardFiltersContext';

export interface PendingSupportTicketsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  scope?: DashboardScope;
}

const PendingSupportTickets: React.FC<PendingSupportTicketsProps> = ({
  limit: limitProp = 5,
  embedded,
  settings,
  scope = 'myWork',
}) => {
  const limit = typeof settings?.limit === 'number' ? settings.limit : limitProp;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { supportTickets, isLoading } = useSupportTickets();
  const { colors } = useTheme();
  const cardClass = embedded ? 'min-w-0 flex-1 flex flex-col min-h-0' : `${colors.surface} rounded-xl shadow-sm p-5 ${colors.border} border`;
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1';

  const ticketsArray = Array.isArray(supportTickets) ? supportTickets : [];
  const pending = ticketsArray
    .filter((ticket: SupportTicket) => {
      if (scope === 'myWork' && user?.id != null && ticket.assignedToId !== user.id) return false;
      // Filter tickets that are not closed/resolved
      const statusDescr = ticket.statusDescr?.pts_descr?.toLowerCase() || '';
      const isClosed = statusDescr.includes('closed') || 
                      statusDescr.includes('resolved') || 
                      statusDescr.includes('completed');
      return !isClosed;
    })
    .sort((a: SupportTicket, b: SupportTicket) => {
      // Sort by priority (higher first), then by created date (newest first)
      const priorityA = a.priority || 0;
      const priorityB = b.priority || 0;
      if (priorityB !== priorityA) return priorityB - priorityA;
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    })
    .slice(0, limit);

  const getPriorityColor = (priority?: number) => {
    if (!priority) return 'bg-gray-500';
    if (priority >= 4) return 'bg-red-500';
    if (priority >= 3) return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  const getPriorityLabel = (priority?: number) => {
    if (!priority) return 'Normal';
    if (priority >= 4) return 'Critical';
    if (priority >= 3) return 'High';
    if (priority >= 2) return 'Medium';
    return 'Low';
  };

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3`}>Pending Support Tickets</h2>
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`flex items-center space-x-3 sm:space-x-4 pb-2 sm:pb-3 ${colors.border} border-b last:border-0`}>
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded ${colors.surfaceHover} animate-pulse flex-shrink-0`}></div>
              <div className="flex-1 min-w-0">
                <div className={`h-3 sm:h-4 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1 sm:mb-2`}></div>
                <div className={`h-2.5 sm:h-3 ${colors.surfaceHover} animate-pulse rounded w-1/2`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>Pending Support Tickets</h2>
        <button
          onClick={() => navigate('/support-ticket')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {pending.length > 0 ? (
          pending.map((ticket: SupportTicket) => {
            const createdDate = ticket.createdAt ? new Date(ticket.createdAt) : null;
            
            return (
              <div
                key={ticket.id}
                onClick={() => navigate(`/support-ticket/${ticket.id}`)}
                className={`flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0`}
              >
                <div className="flex-shrink-0 mt-1">
                  <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${getPriorityColor(ticket.priority)}`} title={getPriorityLabel(ticket.priority)}></div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs sm:text-sm font-medium ${colors.text} line-clamp-2 mb-1`}>{ticket.title || 'Untitled Ticket'}</p>
                  {ticket.statusDescr?.pts_descr && (
                    <p className={`text-[10px] sm:text-xs ${colors.textSecondary} mb-1`}>{ticket.statusDescr.pts_descr}</p>
                  )}
                  
                  <div className={`flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2 text-[10px] sm:text-xs ${colors.textMuted}`}>
                    {ticket.assignedTo && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="truncate">{ticket.assignedTo.userDisplayName || ticket.assignedTo.userFirstName || 'Unassigned'}</span>
                      </div>
                    )}
                    
                    {ticket.client?.clientName && (
                      <div className="flex items-center gap-1">
                        <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="truncate">{ticket.client.clientName.length > 20 ? ticket.client.clientName.substring(0, 20) + '...' : ticket.client.clientName}</span>
                      </div>
                    )}
                    
                    {createdDate && (
                      <div className="flex items-center gap-1">
                        <svg className={`w-3 h-3 flex-shrink-0 ${colors.textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{createdDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center py-6 sm:py-8 ${colors.textMuted}`}>
            <svg className={`w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-3 ${colors.borderLight}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm sm:text-base">No pending tickets</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingSupportTickets;

